import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography, Chip, Alert
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getApartmentUsers, getFunctionHallBookings, checkHallAvailability, createFunctionHallBooking, deleteFunctionHallBooking, USE_MOCK_DATA } from '../services/api';
import { mockApartments, mockApartmentUsers, mockFunctionHallBookings } from '../services/mockData';

const slotOptions = [
  { value: 'FullDay', label: 'Full Day' },
  { value: 'HalfDay-Morning', label: 'Half Day - Morning' },
  { value: 'HalfDay-Evening', label: 'Half Day - Evening' }
];

const statusColors = { Paid: 'success', Pending: 'warning' };

export default function FunctionHall() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [apartmentUsers, setApartmentUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [open, setOpen] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [availability, setAvailability] = useState(null);
  const [form, setForm] = useState({
    apartmentUserId: '', flatNumber: '', bookedByName: '',
    bookingDate: '', slotType: 'FullDay', amount: '', paymentStatus: 'Pending', notes: ''
  });
  const { isAdmin } = useAuth();

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (USE_MOCK_DATA) {
      const userApartments = [mockApartments[0]];
      setApartments(userApartments);
      setSelectedApartment(userApartments[0].id);
      return;
    }
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    if (USE_MOCK_DATA) {
      setBookings(mockFunctionHallBookings);
      setApartmentUsers(mockApartmentUsers);
      return;
    }
    getFunctionHallBookings(selectedApartment, month, year).then(res => setBookings(res.data)).catch(() => {});
    getApartmentUsers(selectedApartment).then(res => setApartmentUsers(res.data)).catch(() => {});
  }, [selectedApartment, month, year]);

  const handleDateChange = async (date) => {
    setForm({ ...form, bookingDate: date });
    setAvailability(null);
    setBookingError('');

    if (!date || !selectedApartment) return;

    try {
      const res = await checkHallAvailability(selectedApartment, date);
      setAvailability(res.data);

      // If full day is booked, show error
      if (res.data.isFullDayBooked) {
        setBookingError('This date is fully booked (Full Day). Please select another date.');
      }
    } catch {
      // Silently fail - user can still try to book
    }
  };

  const handleSubmit = async () => {
    setBookingError('');

    if (!form.apartmentUserId || !form.bookingDate || !form.bookedByName) {
      setBookingError('Please fill all required fields (Resident, Date, Booked By)');
      return;
    }

    if (USE_MOCK_DATA) {
      const au = apartmentUsers.find(u => u.id === parseInt(form.apartmentUserId));
      setBookings(prev => [...prev, {
        id: Date.now(), apartmentId: selectedApartment, apartmentName: 'Sunshine Residency',
        apartmentUserId: parseInt(form.apartmentUserId), flatNumber: form.flatNumber,
        bookedByName: form.bookedByName, bookingDate: form.bookingDate,
        slotType: form.slotType, amount: form.amount ? parseFloat(form.amount) : null,
        paymentStatus: form.paymentStatus, notes: form.notes || null,
        createdByUser: 'Debug Admin', createdAt: new Date().toISOString()
      }]);
    } else {
      try {
        await createFunctionHallBooking({
          apartmentId: selectedApartment,
          apartmentUserId: parseInt(form.apartmentUserId),
          flatNumber: form.flatNumber,
          bookedByName: form.bookedByName,
          bookingDate: form.bookingDate,
          slotType: form.slotType,
          amount: form.amount ? parseFloat(form.amount) : null,
          paymentStatus: form.paymentStatus,
          notes: form.notes || null
        });
        getFunctionHallBookings(selectedApartment, month, year).then(res => setBookings(res.data));
      } catch (err) {
        setBookingError(err.response?.data || 'Booking failed. This slot may already be taken.');
        return;
      }
    }
    handleDialogClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Cancel this booking?')) {
      if (USE_MOCK_DATA) {
        setBookings(prev => prev.filter(b => b.id !== id));
      } else {
        await deleteFunctionHallBooking(id);
        getFunctionHallBookings(selectedApartment, month, year).then(res => setBookings(res.data));
      }
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setBookingError('');
    setAvailability(null);
    setForm({ apartmentUserId: '', flatNumber: '', bookedByName: '', bookingDate: '', slotType: 'FullDay', amount: '', paymentStatus: 'Pending', notes: '' });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Function Hall Booking</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Apartment</InputLabel>
            <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
              {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {Array.from({ length: 5 }, (_, i) => <MenuItem key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button variant="contained" startIcon={<Add />} fullWidth onClick={() => setOpen(true)}>Book Hall</Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Flat No</TableCell>
              <TableCell>Booked By</TableCell>
              <TableCell>Slot</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              {isAdmin() && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>No bookings for this month</Typography>
                </TableCell>
              </TableRow>
            ) : (
              bookings.map(b => (
                <TableRow key={b.id}>
                  <TableCell>{new Date(b.bookingDate).toLocaleDateString()}</TableCell>
                  <TableCell>{b.flatNumber}</TableCell>
                  <TableCell>{b.bookedByName}</TableCell>
                  <TableCell>
                    <Chip
                      label={b.slotType === 'FullDay' ? 'Full Day' : b.slotType === 'HalfDay-Morning' ? 'Morning' : 'Evening'}
                      size="small"
                      color={b.slotType === 'FullDay' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">{b.amount ? `₹${b.amount.toFixed(2)}` : '—'}</TableCell>
                  <TableCell>
                    <Chip label={b.paymentStatus} size="small" color={statusColors[b.paymentStatus] || 'default'} />
                  </TableCell>
                  <TableCell>{b.notes || '—'}</TableCell>
                  {isAdmin() && (
                    <TableCell>
                      <IconButton onClick={() => handleDelete(b.id)} color="error" title="Cancel Booking"><Delete /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Book Function Hall</DialogTitle>
        <DialogContent>
          {bookingError && <Alert severity="error" sx={{ mb: 2 }}>{bookingError}</Alert>}

          <FormControl fullWidth margin="normal">
            <InputLabel>Flat / Resident</InputLabel>
            <Select value={form.apartmentUserId} label="Flat / Resident" onChange={(e) => {
              const selected = apartmentUsers.find(au => au.id === e.target.value);
              setForm({ ...form, apartmentUserId: e.target.value, flatNumber: selected?.flatNumber || '', bookedByName: selected?.userName || '' });
            }}>
              {apartmentUsers.map(au => <MenuItem key={au.id} value={au.id}>{au.flatNumber} - {au.userName}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Flat Number" margin="dense" value={form.flatNumber} onChange={(e) => setForm({ ...form, flatNumber: e.target.value })} />
          <TextField fullWidth label="Booked By (Name)" margin="dense" value={form.bookedByName} onChange={(e) => setForm({ ...form, bookedByName: e.target.value })} />
          <TextField fullWidth label="Booking Date" type="date" margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: today }}
            value={form.bookingDate}
            onChange={(e) => handleDateChange(e.target.value)} />

          {/* Show availability info */}
          {availability && (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
                Availability for {new Date(form.bookingDate).toLocaleDateString()}:
              </Typography>
              <Chip label="Full Day" size="small" sx={{ mr: 0.5 }}
                color={availability.availableSlots?.fullDay ? 'success' : 'error'}
                variant={availability.availableSlots?.fullDay ? 'outlined' : 'filled'} />
              <Chip label="Morning" size="small" sx={{ mr: 0.5 }}
                color={availability.availableSlots?.halfDayMorning ? 'success' : 'error'}
                variant={availability.availableSlots?.halfDayMorning ? 'outlined' : 'filled'} />
              <Chip label="Evening" size="small"
                color={availability.availableSlots?.halfDayEvening ? 'success' : 'error'}
                variant={availability.availableSlots?.halfDayEvening ? 'outlined' : 'filled'} />
              {availability.bookings && availability.bookings.length > 0 && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  Booked by: {availability.bookings.map(b => `${b.flatNumber} (${b.slotType})`).join(', ')}
                </Typography>
              )}
            </Box>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Slot</InputLabel>
            <Select value={form.slotType} label="Slot" onChange={(e) => setForm({ ...form, slotType: e.target.value })}>
              {slotOptions.map(s => (
                <MenuItem key={s.value} value={s.value}
                  disabled={availability && (
                    (s.value === 'FullDay' && !availability.availableSlots?.fullDay) ||
                    (s.value === 'HalfDay-Morning' && !availability.availableSlots?.halfDayMorning) ||
                    (s.value === 'HalfDay-Evening' && !availability.availableSlots?.halfDayEvening)
                  )}>
                  {s.label} {availability && (
                    (s.value === 'FullDay' && !availability.availableSlots?.fullDay) ||
                    (s.value === 'HalfDay-Morning' && !availability.availableSlots?.halfDayMorning) ||
                    (s.value === 'HalfDay-Evening' && !availability.availableSlots?.halfDayEvening)
                  ) ? '(Booked)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth label="Amount (Optional)" type="number" margin="normal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Payment Status</InputLabel>
            <Select value={form.paymentStatus} label="Payment Status" onChange={(e) => setForm({ ...form, paymentStatus: e.target.value })}>
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Notes (Optional)" margin="normal" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Book</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
